import * as types from '../mutation-types';

export default {
  namespaced: true,
  // -----------------------------------------------------------------
  state: {
    maxRows: 0,
    tenants: {},
    roles: {},
    permissions: {},
    countries: {},
    shops: {},
    devices: {},
    shopGroups: {},
    firewalls: {},
    lotterySystems: {},
    users: {},
    lotteryEvents: {},
    players: {},
    onlinePlayers: {},
    thirdPartyPlayers: {},
    terminals: {},
    companies: {},
    products: {},
    banks: {},
    operators: {},
    subtypes: {},
  },
  // -----------------------------------------------------------------
  getters: {
    // getters and computed props on the audits data
    tenants: state => state.tenants,
    companies: state => state.companies,
    roles: state => state.roles,
    permissions: state => state.permissions,
    countries: state => state.countries,
    shops: state => state.shops,
    devices: state => state.devices,
    shopGroups: state => state.shopGroups,
    firewalls: state => state.firewalls,
    users: state => state.users,
    lotterySystems: state => state.lotterySystems,
    lotteryEvents: state => state.lotteryEvents,
    players: state => state.players,
    onlinePlayers: state => state.onlinePlayers,
    thirdPartyPlayers: state => state.thirdPartyPlayers,
    terminals: state => state.terminals,
    products: state => state.products,
    banks: state => state.banks,
    operators: state => state.operators,
    subtypes: state => state.subtypes,

    thirdPartyPlayerByID: state => id => state.thirdPartyPlayers[id],
    playerByID: state => id => state.players[id],
  },
  // -----------------------------------------------------------------
  mutations: {
    [types.CACHE_ON_DEMAND_INVALIDATE](state, key) {
      state[key] = {};
    },
    [types.CACHE_ON_DEMAND_LOAD](state, {
      key,
      objects
    }) {
      state[key] = {
        ...state[key],
        ...objects
      }
    }
  },
  // -----------------------------------------------------------------
  actions: {
    fetch: (context, {
      key,
      ids
    }) => {
      const item = context.state[key];
      const toFetch = ids.filter(id => !(id in item));

      if (toFetch.length == 0) {
        return new Promise((resolve, reject) => {
          resolve(context.state[key]);
        });
      }

      const paginationData = {
        rowsPerPage: context.state.maxRows,
        page: 0,
        sortDirection: ["ASC"],
      };

      switch (key) {
        case "permissions":
          paginationData.sortBy = ["name"];
          paginationData.fields = ["name"];
          break;
        case "tenant":
        case "tenants":
          paginationData.sortBy = ["brand_name"];
          paginationData.fields = ["id", "brand_name", "company_id"];
          break;
        case "companies":
          paginationData.sortBy = ["name"];
          paginationData.fields = ["id", "name"];
          break;
        case "shops":
          paginationData.sortBy = ["name"];
          paginationData.fields = ["id", "name", "tenant_id", "code", "company_id", "active"];
          break;
        case "devices":
          paginationData.sortBy = ["name"];
          paginationData.fields = ["id", "uuid", "name", "type", "tenant_id", "shop_id"];
          break;
        case "shopGroups":
          paginationData.sortBy = ["name"];
          paginationData.fields = ["id", "name", "tenant_id", "shop_ids", "firewall_ruleset_id"];
          break;
        case "users":
          paginationData.sortBy = ["username"];
          paginationData.fields = ["id", "username"];
          break;
        case "players":
          paginationData.sortBy = ["name"];
          paginationData.fields = ["id", "name", "surname"];
          break;
        case "onlinePlayers":
          paginationData.sortBy = ["email"];
          paginationData.fields = ["id", "email", "third_party_player_ids"];
          break;
        case "thirdPartyPlayers":
          paginationData.sortBy = ["name"];
          paginationData.fields = ["id", "tenant_id", "shop_id", "name", "surname", "email", "external_id"];
          break;
        case "lotteryEvents":
          paginationData.sortBy = ["system_name"];
          paginationData.fields = ["id", "system_name", "schedule_time", "n", "r"];
          break;
        case "terminals":
          paginationData.sortBy = ["app_id"];
          paginationData.fields = ["id", "shop_id", "app_id", "name"];
          break;
        case "banks":
          paginationData.sortBy = ["name"];
          paginationData.fields = ["id", "name", "account"];
          break;
        case "operators":
          paginationData.sortBy = ["full_name"];
          paginationData.fields = ["id", "full_name", "cnp"];
          break;
        case "subtypes":
          paginationData.sortBy = ["name"];
          paginationData.fields = ["id", "name", "observation"];
          break;
        default:
          paginationData.sortBy = ["name"];
          paginationData.fields = ["id", "name"];
      }

      const api = {
        "tenants": "tenant/fetchByIDs",
        "companies": "company/fetchByIDs",
        "roles": "role/fetchByIDs",
        "countries": "country/fetchByIDs",
        "permissions": "permissions/fetchByIDs",
        "shops": "shop/fetchByIDs",
        "devices": "device/fetchByIDs",
        "firewalls": "firewall/fetchByIDs",
        "shopGroups": "shopGroup/fetchByIDs",
        "lotterySystems": "lotterySystem/fetchByIDs",
        "lotteryEvents": "lotteryEvent/fetchByIDs",
        "users": "user/fetchByIDs",
        "players": "player/fetchByIDs",
        "onlinePlayers": "onlinePlayer/fetchByIDs",
        "thirdPartyPlayers": "thirdPartyPlayer/fetchByIDs",
        "terminals": "terminal/fetchByIDs",
        "products": "product/fetchByIDs",
        "banks": "bank/fetchByIDs",
        "operators": "operator/fetchByIDs",
        "subtypes": "subtype/fetchByIDs",
      };

      return new Promise((resolve, reject) => {
        context.dispatch(api[key], {
          ...paginationData,
          ids: toFetch
        }, {
          root: true
        })
          .then((resp) => {
            if (resp.success) {
              if (!resp.data.objects) resp.data.objects = {};
              context.commit(types.CACHE_ON_DEMAND_LOAD, {
                key: key,
                objects: resp.data.objects
              });
              resolve({
                ...context.state[key],
                ...resp.data.objects
              });
            }
          }).catch((err) => {
            reject(err);
          })
      });
    },
  }
};
